/** @format */

import React from "react";

// Shown while a lazily loaded page chunk downloads. Without a fallback the
// whole app (header included) disappears during the download, which on a
// slow or cache-less connection looks like a dead "Continue" button.
export const RouteLoading = () => (
	<div className="min-h-[60vh] flex flex-col items-center justify-center text-gray-500">
		<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-3"></div>
		<p className="text-sm">Loading...</p>
	</div>
);

const isChunkError = (error) => {
	const text = `${error?.name || ""} ${error?.message || ""}`;
	return (
		/ChunkLoadError/i.test(text) ||
		/Loading (CSS )?chunk [\w-]+ failed/i.test(text) ||
		/Failed to fetch dynamically imported module/i.test(text)
	);
};

// Catches a failed lazy import. The usual cause is a deploy that replaced the
// hashed chunk files while a visitor still has the old index.html: the import
// 404s, React throws, and the page goes blank. One automatic reload fetches the
// new index.html; if that still fails the visitor gets a Reload button.
export class ChunkErrorBoundary extends React.Component {
	constructor(props) {
		super(props);
		this.state = { error: null };
	}

	static getDerivedStateFromError(error) {
		return { error };
	}

	componentDidCatch(error) {
		if (!isChunkError(error)) return;
		const key = "ocs-chunk-reload";
		let reloaded = false;
		try {
			reloaded = sessionStorage.getItem(key) === "1";
			if (!reloaded) sessionStorage.setItem(key, "1");
		} catch (e) {
			// storage blocked: fall through to the manual button
		}
		if (!reloaded) window.location.reload();
	}

	render() {
		if (!this.state.error) return this.props.children;
		return (
			<div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4 bg-[#FAFAF7] text-[#141414]">
				<p className="font-instrument text-[32px] font-normal leading-none mb-3">
					This page could not be loaded.
				</p>
				<p className="text-sm text-[#5E5E5A] mb-6">
					Your selections are saved. Please reload to continue.
				</p>
				<button
					type="button"
					onClick={() => window.location.reload()}
					className="min-h-[48px] px-7 bg-[#141414] text-[#FAFAF7] text-sm font-medium"
				>
					Reload
				</button>
			</div>
		);
	}
}

export default ChunkErrorBoundary;
